/**
 * Cryptographic utilities for secure password handling.
 * 
 * Passwords are hashed client-side with SHA-256 before transmission
 * to prevent plaintext passwords from appearing in network logs.
 * 
 * The server applies bcrypt on top of the SHA-256 hash for storage.
 */

/**
 * Prefix used to identify passwords that were pre-hashed by the client.
 * This allows the server to handle both pre-hashed and legacy plaintext passwords.
 */
const SHA256_PREFIX = '$sha256$';

/**
 * Hash a string using SHA-256.
 * Uses the Web Crypto API which is available in all modern browsers.
 * 
 * @param input - The string to hash
 * @returns Promise resolving to the lowercase hex string of the SHA-256 hash
 */
export async function sha256Hash(input: string): Promise<string> {
    // Encode the input as UTF-8
    const encoder = new TextEncoder();
    const data = encoder.encode(input);
    
    // Hash using SHA-256 (built into all modern browsers)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    
    // Convert ArrayBuffer to hex string
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
    
    return hashHex;
}

/**
 * Hash a password for secure transmission to the server.
 * 
 * The password is hashed with SHA-256 and prefixed with "$sha256$"
 * so the server knows it's already hashed and doesn't need to hash it again.
 * 
 * This ensures:
 * - Plaintext password never appears in network requests
 * - Plaintext password never appears in server logs
 * - Server can still apply bcrypt for secure storage
 * 
 * @param password - The plaintext password to hash
 * @returns Promise resolving to the prefixed SHA-256 hash (e.g., "$sha256$abc123...")
 * 
 * @example
 * const hashedPassword = await hashPasswordForTransmission('myPassword123');
 * // Returns: "$sha256$ef92b778bafe771e89245b89ecbc08a44a4e166c06659911881f383d4473e94f"
 */
export async function hashPasswordForTransmission(password: string): Promise<string> {
    const hash = await sha256Hash(password);
    return `${SHA256_PREFIX}${hash}`;
}

/**
 * Check if a password string is already pre-hashed.
 * 
 * @param password - The password string to check
 * @returns true if the password starts with the SHA-256 prefix
 */
export function isPreHashed(password: string): boolean {
    return password.startsWith(SHA256_PREFIX);
}

/**
 * Extract the hash from a pre-hashed password string.
 * 
 * @param preHashedPassword - The pre-hashed password with prefix
 * @returns The SHA-256 hash without the prefix, or the original string if not pre-hashed
 */
export function extractHash(preHashedPassword: string): string {
    if (isPreHashed(preHashedPassword)) {
        return preHashedPassword.substring(SHA256_PREFIX.length);
    }
    return preHashedPassword;
}

// ═══════════════════════════════════════════════════════════════════════════════
// AES-GCM localStorage encryption
// ═══════════════════════════════════════════════════════════════════════════════
// Used for encrypting sensitive data at rest in localStorage (e.g. co-applicant
// financial estimates in the explorer). NOT a substitute for server-side
// encryption — this prevents casual inspection of localStorage and ties data
// to a specific application context.
// ═══════════════════════════════════════════════════════════════════════════════

const STORAGE_KEY_PREFIX = 'hipotech_enc_';

/**
 * Derive a CryptoKey from a string seed using PBKDF2.
 * The seed should be application-scoped (e.g. applicationId) so data
 * from one application can't be decrypted in another context.
 */
async function deriveKey(seed: string): Promise<CryptoKey> {
    const encoder = new TextEncoder();
    const keyMaterial = await crypto.subtle.importKey(
        'raw', encoder.encode(seed), 'PBKDF2', false, ['deriveKey']
    );
    // Static salt is fine here — we're defending against casual localStorage
    // inspection, not a determined attacker. The seed (applicationId) provides
    // the per-context variation.
    const salt = encoder.encode('hipotech-local-v1');
    return crypto.subtle.deriveKey(
        { name: 'PBKDF2', salt, iterations: 100_000, hash: 'SHA-256' },
        keyMaterial,
        { name: 'AES-GCM', length: 256 },
        false,
        ['encrypt', 'decrypt']
    );
}

/**
 * Encrypt a JSON-serializable value and store it in localStorage.
 * 
 * @param key - Storage key (will be prefixed)
 * @param data - Any JSON-serializable value
 * @param seed - Key derivation seed (e.g. applicationId)
 */
export async function encryptToStorage<T>(key: string, data: T, seed: string): Promise<void> {
    try {
        const cryptoKey = await deriveKey(seed);
        const encoder = new TextEncoder();
        const plaintext = encoder.encode(JSON.stringify(data));
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const ciphertext = await crypto.subtle.encrypt(
            { name: 'AES-GCM', iv },
            cryptoKey,
            plaintext
        );
        // Store as base64: iv (12 bytes) + ciphertext
        const combined = new Uint8Array(iv.length + new Uint8Array(ciphertext).length);
        combined.set(iv, 0);
        combined.set(new Uint8Array(ciphertext), iv.length);
        const b64 = btoa(Array.from(combined, b => String.fromCharCode(b)).join(''));
        localStorage.setItem(`${STORAGE_KEY_PREFIX}${key}`, b64);
    } catch (e) {
        // Silently fail — localStorage encryption is best-effort
        console.warn('Failed to encrypt to storage:', e);
    }
}

/**
 * Decrypt a value from localStorage.
 * 
 * @param key - Storage key (will be prefixed)
 * @param seed - Same seed used for encryption
 * @returns The decrypted value, or null if not found / decryption fails
 */
export async function decryptFromStorage<T>(key: string, seed: string): Promise<T | null> {
    try {
        const raw = localStorage.getItem(`${STORAGE_KEY_PREFIX}${key}`);
        if (!raw) return null;

        const cryptoKey = await deriveKey(seed);
        const combined = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
        const iv = combined.slice(0, 12);
        const ciphertext = combined.slice(12);
        const plaintext = await crypto.subtle.decrypt(
            { name: 'AES-GCM', iv },
            cryptoKey,
            ciphertext
        );
        const decoder = new TextDecoder();
        return JSON.parse(decoder.decode(plaintext)) as T;
    } catch (e) {
        // Decryption failed — likely seed changed or data corrupted
        console.warn('Failed to decrypt from storage:', e);
        return null;
    }
}

/**
 * Remove an encrypted value from localStorage.
 */
export function removeFromStorage(key: string): void {
    try {
        localStorage.removeItem(`${STORAGE_KEY_PREFIX}${key}`);
    } catch { /* localStorage unavailable */ }
}

/**
 * Clear all hipotech encrypted storage entries.
 */
export function clearEncryptedStorage(): void {
    try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith(STORAGE_KEY_PREFIX));
        keys.forEach(k => localStorage.removeItem(k));
    } catch { /* localStorage unavailable */ }
}